import type { PrayerDay, IslamicEvent } from "../lib/types";
import { useLocale, hijriMonthName, eventName, WEEKDAYS_SHORT } from "../lib/i18n";

export function HijriCalendarGrid(props: {
  days: PrayerDay[];
  events: IslamicEvent[];
  activeDateISO?: string;
}) {
  const { t, locale } = useLocale();
  const days = props.days;
  if (days.length === 0) return null;

  const firstISO = days[0].dateGregorianISO;
  const [y, m] = firstISO.split("-").map(Number);
  const firstWeekday = new Date(y, m - 1, 1).getDay();
  const pad = (firstWeekday + 6) % 7;

  const cells: Array<PrayerDay | null> = new Array<PrayerDay | null>(pad).fill(null).concat(days);

  const byDate = new Map<string, IslamicEvent[]>();
  for (const e of props.events) {
    const arr = byDate.get(e.approxGregorianRange.startISO) ?? [];
    arr.push(e);
    byDate.set(e.approxGregorianRange.startISO, arr);
  }

  const weekday = WEEKDAYS_SHORT[locale];

  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="h2" style={{ marginBottom: 10 }}>{t.calendarView}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginBottom: 8 }}>
        {weekday.map((w) => (
          <div key={w} className="p" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.03em" }}>
            {w}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
        {cells.map((d, idx) => {
          if (!d) {
            return (
              <div
                key={`empty-${idx}`}
                style={{
                  minHeight: 78,
                  borderRadius: 14,
                  background: "var(--panel-cell-muted)",
                  border: "1px dashed var(--border)"
                }}
              />
            );
          }

          const events = byDate.get(d.dateGregorianISO) ?? [];
          const isActive = props.activeDateISO === d.dateGregorianISO;
          return (
            <div
              key={d.dateGregorianISO}
              style={{
                minHeight: 78,
                borderRadius: 14,
                border: isActive ? "1px solid var(--highlight-border)" : "1px solid var(--border)",
                background: isActive ? "var(--highlight-bg)" : events.length ? "var(--highlight-event-bg)" : "var(--panel-cell)",
                padding: 10,
                overflow: "hidden",
                boxShadow: isActive ? "var(--highlight-shadow)" : undefined
              }}
            >
              <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
                <div style={{ fontWeight: 800 }}>{Number(d.dateGregorianISO.slice(-2))}</div>
                <div className="p" style={{ fontSize: 12 }}>
                  {d.dateHijri.day}
                </div>
              </div>
              <div className="p" style={{ fontSize: 12, marginTop: 6 }}>
                {hijriMonthName(locale, d.dateHijri.month)}
              </div>
              {events.length ? (
                <div className="p" style={{ fontSize: 11, marginTop: 6, color: "var(--highlight-event-text)" }}>
                  {eventName(locale, events[0].hijriDate.month, events[0].hijriDate.day, events[0].name)}
                  {events.length > 1 ? ` +${events.length - 1}` : ""}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="p" style={{ fontSize: 12, marginTop: 10 }}>
        {t.calendarGridNote}
      </div>
    </div>
  );
}
